import React from 'react';
import { Link } from 'react-router-dom';
import PageContainer from '../components/PageContainer';

const DataManagement: React.FC = () => {
  const dataTypes = [
    {
      title: 'Programs',
      desc: 'Manage academic programs, faculties, and program aliases',
      path: '/data/programs',
      count: null,
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M4.26 10.147a60.436 60.436 0 00-.491 6.347A48.627 48.627 0 0112 20.904a48.627 48.627 0 018.232-4.41 60.46 60.46 0 00-.491-6.347m-15.482 0a50.57 50.57 0 00-2.658-.813A59.905 59.905 0 0112 3.493a59.902 59.902 0 0110.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0A50.697 50.697 0 0112 13.489a50.702 50.702 0 017.74-3.342" />
        </svg>
      ),
      iconColor: 'text-primary-700 bg-primary-50',
      features: ['Arabic & English names', 'Faculty assignment', 'Alias management', 'Bulk upload'],
    },
    {
      title: 'Contacts',
      desc: 'University contact information with office hours and locations',
      path: '/data/contacts',
      count: null,
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
        </svg>
      ),
      iconColor: 'text-emerald-600 bg-emerald-50',
      features: ['Phone & email', 'Office hours', 'Location info', 'Bulk upload'],
    },
    {
      title: 'Departments',
      desc: 'Academic departments with leadership and contact details',
      path: '/data/departments',
      count: null,
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21m-3.75 3.75h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008z" />
        </svg>
      ),
      iconColor: 'text-primary-600 bg-primary-50',
      features: ['Department head', 'Contact email', 'Contact phone', 'Bulk upload'],
    },
    {
      title: 'Admission Grades',
      desc: 'Admission requirements and minimum grades by year and track',
      path: '/data/admission-grades',
      count: null,
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
        </svg>
      ),
      iconColor: 'text-amber-600 bg-amber-50',
      features: ['By program', 'Yearly grades', 'Admission types', 'Track-based'],
    },
  ];

  return (
    <PageContainer
      title="Data"
      subtitle="Manage the structured information behind the chatbot's answers."
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {dataTypes.map((dt) => (
          <Link key={dt.path} to={dt.path} className="card-hover group overflow-hidden">
            <div className="p-5">
              <div className="flex items-start gap-4">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center transition-colors flex-shrink-0 ${dt.iconColor}`}>
                  {dt.icon}
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="text-sm font-semibold text-primary-800 group-hover:text-primary-700 transition-colors">{dt.title}</h3>
                  <p className="text-xs text-primary-500 mt-0.5">{dt.desc}</p>
                </div>
                <svg className="w-4 h-4 text-primary-300 group-hover:text-primary-400 transition-colors flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                </svg>
              </div>
            </div>
            <div className="px-5 pb-4">
              <div className="flex flex-wrap gap-1.5">
                {dt.features.map((f) => (
                  <span key={f} className="px-2 py-0.5 rounded-full bg-primary-50 border border-primary-100 text-xs text-primary-500 font-medium">
                    {f}
                  </span>
                ))}
              </div>
            </div>
          </Link>
        ))}
      </div>

      <div className="mt-6 flex items-start gap-3 p-4 rounded-xl bg-primary-50/50 border border-primary-100">
        <svg className="w-4 h-4 text-primary-500 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" />
        </svg>
        <div>
          <h4 className="text-xs font-semibold text-primary-800">Bulk Operations</h4>
          <p className="text-xs text-primary-600 mt-0.5">
            Each data type supports bulk upload (CSV/JSON) and bulk delete. Use the buttons in each list view.
            Changes are applied to the chatbot automatically after you save.
          </p>
        </div>
      </div>
    </PageContainer>
  );
};

export default DataManagement;
